import React, { useEffect, useCallback } from "react";
import {
  Card,
  CardContent,
  Dialog,
  DialogScroll,
  DialogTitleActions,
  Flex,
  Type,
  Spinner,
} from "@ebscois/react-component-library";
import { useDispatch, useStore } from "../../../store";
import {
  getFollowedContent,
  unsubscribeFollowedContent,
} from "../../../store/actions/followedContent";
import FollowedContentTable from "../Table";
import FollowedContentErrorMessage from "../ErrorMessage";
import { DialogTitleWrapper, DialogUnfollowErrorIcon } from "./index.styled";
import ErrorBoundary from "../../ErrorBoundary";
import { useEventBus } from "../../../util/useEventBus";

const DIALOG_TITLE_ID = "followedContent-dialog-title";

const FollowedContentDialog = ({ isOpen, onClose }) => {
  const dispatch = useDispatch();
  const {
    followedContent: {
      isFetching,
      isDeleting,
      followedContentViewModel,
      unfollowErrorMessage,
    },
  } = useStore();
  const unfollowError = followedContentViewModel?.content?.unfollowError;

  useEffect(() => {
    getFollowedContent(dispatch);
  }, [dispatch]);

  const eventBus = useEventBus();
  const handleDelete = useCallback(
    (type, id) => unsubscribeFollowedContent(dispatch, type, id, eventBus),
    [dispatch, eventBus]
  );

  const renderBody = () => {
    if (isFetching) {
      return <Spinner size="md" />;
    }

    const error = followedContentViewModel?.error;
    const content = followedContentViewModel?.content;
    if (error || !content) {
      return <FollowedContentErrorMessage error={error} />;
    }

    const {
      followedFeeds,
      noFollowedContentNotification,
      unfollowAction,
    } = content;

    if (noFollowedContentNotification && !followedFeeds?.length) {
      return <Type marginTop="md">{noFollowedContentNotification}</Type>;
    }

    return (
      <FollowedContentTable
        followedFeeds={followedFeeds}
        deleteIcon={unfollowAction?.icon}
        isDeleting={isDeleting}
        onDelete={handleDelete}
        onClose={onClose}
      />
    );
  };

  return (
    <ErrorBoundary team="cd.sirocco">
      <Dialog
        background="black"
        aria-labelledby={DIALOG_TITLE_ID}
        isOpen={isOpen}
        maxWidth="md"
        onRequestClose={onClose}
        showCloseButton
      >
        <DialogTitleWrapper
          borderBottomColor="gray300"
          borderBottomStyle="solid"
          borderBottomWidth="1px"
        >
          <Type
            element="h2"
            fontFamily="secondary"
            fontSize="lg"
            color="gray800"
            id={DIALOG_TITLE_ID}
          >
            {followedContentViewModel?.title}
          </Type>
          <DialogTitleActions onRequestClose={onClose} />
          {unfollowErrorMessage && unfollowError && (
            <Flex container width="100%" marginTop="sm">
              <DialogUnfollowErrorIcon
                icon={{
                  glyph: unfollowError.icon.glyph,
                  size: unfollowError.icon.size,
                  color: unfollowError.icon.color,
                }}
              />
              <Type
                element="p"
                fontSize="sm"
                color="red.base"
                data-testid="unfollow-error-message"
              >
                {unfollowErrorMessage}
              </Type>
            </Flex>
          )}
        </DialogTitleWrapper>
        <DialogScroll style={{ maxHeight: "23rem" }}>
          <Card>
            <CardContent paddingTop="0">{renderBody()}</CardContent>
          </Card>
        </DialogScroll>
      </Dialog>
    </ErrorBoundary>
  );
};
FollowedContentDialog.displayName = "FollowedContentDialog";
export default FollowedContentDialog;
